import { CartAddEvent } from '@theme/events';

const PLACEHOLDER_SVG = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <rect width="100" height="100" fill="var(--color-background-2, #f2f2f2)"/>
    <path d="M20 70 L40 50 L55 62 L75 40 L85 50 L85 80 L15 80 Z" fill="var(--color-background-3, #e2e2e2)"/>
    <circle cx="70" cy="30" r="6" fill="var(--color-background-3, #e2e2e2)"/>
  </svg>
`;

const formatMoney = (cents) => {
  if (typeof cents !== 'number') return '';
  const currency = window.Shopify?.currency?.active || 'USD';
  const locale = document.documentElement.lang || 'en-US';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

class ProductEmbed extends HTMLElement {
  #abortController = new AbortController();
  #product = null;
  #variantId = null;

  connectedCallback() {
    if (this.dataset.hydrated === 'true') return;
    this.dataset.hydrated = 'true';
    this.#renderSkeleton();
    this.#loadProduct();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get handle() {
    return this.getAttribute('handle');
  }

  get buttonText() {
    return this.getAttribute('button-text') || 'Preorder';
  }

  get snippetOverride() {
    return this.getAttribute('snippet');
  }

  get variantIdAttr() {
    return this.getAttribute('variant-id');
  }

  #renderSkeleton() {
    this.innerHTML = `
      <div class="product-embed__card product-embed__card--loading" aria-busy="true">
        <div class="product-embed__media"></div>
        <div class="product-embed__body">
          <div class="product-embed__skeleton product-embed__skeleton--title"></div>
          <div class="product-embed__skeleton product-embed__skeleton--price"></div>
          <div class="product-embed__skeleton product-embed__skeleton--text"></div>
          <div class="product-embed__skeleton product-embed__skeleton--button"></div>
        </div>
      </div>
    `;
  }

  async #loadProduct() {
    const handle = this.handle;
    if (!handle) {
      this.#renderError('Missing product handle.');
      return;
    }

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        signal: this.#abortController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Product not found: ${handle}`);
      this.#product = await response.json();
      this.#variantId = this.#resolveVariantId();
      this.#render();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[product-embed]', error);
      this.#renderError('Product could not be loaded.');
    }
  }

  #resolveVariantId() {
    const explicit = this.variantIdAttr;
    if (explicit && this.#product.variants.some((v) => String(v.id) === String(explicit))) {
      return String(explicit);
    }
    const available = this.#product.variants.find((v) => v.available);
    return String((available || this.#product.variants[0]).id);
  }

  #stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
  }

  #render() {
    const product = this.#product;
    const variant = product.variants.find((v) => String(v.id) === this.#variantId) || product.variants[0];
    const priceHtml = this.#buildPriceMarkup(product, variant);
    const image = product.featured_image
      ? `<img class="product-embed__image" src="${product.featured_image}&width=400" alt="${this.#escape(product.title)}" loading="lazy" width="400" height="400">`
      : `<div class="product-embed__image-placeholder">${PLACEHOLDER_SVG}</div>`;
    const snippet = this.snippetOverride || this.#stripHtml(product.description).slice(0, 180);
    const snippetHtml = snippet
      ? `<p class="product-embed__snippet">${this.#escape(snippet)}${snippet.length >= 180 && !this.snippetOverride ? '…' : ''}</p>`
      : '';
    const canAdd = variant?.available;
    const buttonLabel = canAdd ? this.buttonText : 'Sold out';

    this.innerHTML = `
      <div class="product-embed__card">
        <a class="product-embed__media" href="${product.url}" aria-label="${this.#escape(product.title)}">
          ${image}
        </a>
        <div class="product-embed__body">
          <a class="product-embed__title-link" href="${product.url}">
            <h3 class="product-embed__title">${this.#escape(product.title)}</h3>
          </a>
          <div class="product-embed__price">${priceHtml}</div>
          ${snippetHtml}
          <div class="product-embed__actions">
            <button
              type="button"
              class="product-embed__button button"
              data-role="add"
              ${canAdd ? '' : 'disabled'}
            >
              <span class="product-embed__button-label">${this.#escape(buttonLabel)}</span>
              <span class="product-embed__button-added" aria-hidden="true">Added ✓</span>
            </button>
          </div>
        </div>
      </div>
    `;

    const button = this.querySelector('[data-role="add"]');
    button?.addEventListener('click', this.#handleAdd, { signal: this.#abortController.signal });
  }

  #buildPriceMarkup(product, variant) {
    const price = variant?.price ?? product.price;
    const compare = variant?.compare_at_price ?? product.compare_at_price;
    const on_sale = compare && compare > price;
    const priceStr = formatMoney(price);
    if (on_sale) {
      return `
        <span class="product-embed__price-sale">${priceStr}</span>
        <s class="product-embed__price-compare">${formatMoney(compare)}</s>
      `;
    }
    return `<span>${priceStr}</span>`;
  }

  #escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #renderError(message) {
    this.innerHTML = `
      <div class="product-embed__card product-embed__card--error" role="alert">
        <p>${this.#escape(message)}</p>
      </div>
    `;
  }

  #handleAdd = async (event) => {
    event.preventDefault();
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (button.disabled) return;
    button.disabled = true;

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const formData = new FormData();
    formData.set('id', this.#variantId);
    formData.set('quantity', '1');
    if (sectionIds.size) formData.set('sections', Array.from(sectionIds).join(','));

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();

      if (result.status && result.status !== 200) {
        console.error('[product-embed] add-to-cart error', result);
        this.#flashError(button, result.description || result.message || 'Could not add to cart');
        return;
      }

      let cart = null;
      try {
        const cartResp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        cart = await cartResp.json();
      } catch (e) { /* non-fatal */ }

      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id || `product-embed-${this.#product.id}`, {
          source: 'product-form-component',
          itemCount: 1,
          productId: String(this.#product.id),
          variantId: this.#variantId,
          sections: result.sections,
        })
      );

      button.dataset.added = 'true';
      setTimeout(() => {
        delete button.dataset.added;
        button.disabled = false;
      }, 1600);
    } catch (error) {
      console.error('[product-embed]', error);
      this.#flashError(button, 'Could not add to cart');
    }
  };

  #flashError(button, message) {
    const original = button.querySelector('.product-embed__button-label')?.textContent;
    const label = button.querySelector('.product-embed__button-label');
    if (label) label.textContent = message;
    setTimeout(() => {
      if (label && original) label.textContent = original;
      button.disabled = false;
    }, 2500);
  }
}

if (!customElements.get('product-embed')) {
  customElements.define('product-embed', ProductEmbed);
}
